import { FC, MouseEventHandler } from 'react';
import { MenuItem, styled, Typography } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useAppSelector } from '@/redux/hooks';
import { Launch } from '@/redux/slices/focusNewScheduleSlice';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';
import { OverflowMenu, PolarisIcon } from '@/exports/columnCommonComponents';
import { getIncrementalLaunchDisplayValues } from '@/components/Focus/utils/incrementalLaunchWorkspace';
import { formatDateTimePartsLocal } from '@/components/Focus/utils/newScheduleAvails';
import LaunchTypeChip from './AddIncrementalLaunch/LaunchTypeChip';
import IncrementalLaunchAvailChip from './IncrementalLaunchAvailChip';
import useConfirmationPrompt from '@/components/_hooks/useConfirmationPrompt';

type Option = { label: string; value: string };
export type LaunchType = 'first-time' | 'incremental';

export type IncrementalLaunchDetailsRowProps = {
  launch: Launch;
  releaseDate: string;
  releaseTime: string;
  territoryItems: Option[];
  dubItems: Option[];
  subItems: Option[];
  versionItems: Option[];
  isEditing: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
};

const ROW_GRID_COLUMNS = '220px 220px 220px minmax(140px,1fr) minmax(140px,1fr) minmax(140px,1fr) 48px';

const IncrementalLaunchDetailsRow: FC<IncrementalLaunchDetailsRowProps> = ({
  launch,
  releaseDate,
  releaseTime,
  territoryItems,
  dubItems,
  subItems,
  versionItems,
  isEditing,
  onEdit,
  onDelete
}) => {
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);
  const { dialog, dialogVisible, setDialogVisible } = useConfirmationPrompt({
    title: 'Confirm incremental launch delete',
    message: 'Are you sure you want to delete this incremental launch item? This action is irreversible.',
    buttons: {
      submitText: 'Confirm delete',
      cancelText: 'Cancel'
    },
    buttonColor: 'error',
    onCancel: () => setDialogVisible(false),
    onConfirm: () => {
      if (onDelete) {
        onDelete();
      }
    }
  });

  const isIncrementalLaunch = launch.type === 'incremental';
  const territoriesDraft = launch.incrementalLaunchRightsDraftByTab.territories;
  const audioDraft = launch.incrementalLaunchRightsDraftByTab.audio;
  const timedtextDraft = launch.incrementalLaunchRightsDraftByTab.timedtext;
  // NATALIA: Editing versions disabled
  // const versionsDraft = launch.incrementalLaunchVersions;
  const releaseTimeDraft = launch.incrementalLaunchTimingValue;

  const promptDelete: MouseEventHandler<HTMLLIElement> = (e) => {
    e.stopPropagation();
    setDialogVisible(true);
  };

  const offsetReleaseTime = releaseTimeDraft.releaseTime ? formatDateTimePartsLocal(releaseTimeDraft.releaseTime.toISOString()).time : '';

  const { date, time } = getIncrementalLaunchDisplayValues({
    offsetUnit: releaseTimeDraft.offsetUnit,
    offsetValue: releaseTimeDraft.offsetValue,
    offsetReleaseTime,
    releaseDate,
    releaseTime,
    isIncrementalLaunch
  });

  return (
    <StyledRow isEditing={+isEditing}>
      <StyledLeftCell>
        <LaunchTypeChip type={launch.type} />
      </StyledLeftCell>
      <StyledLeftCell>
        {/* isIncrementalLaunch ? (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="rocket_launch" color="white" />}
            label="All Episodes"
            launchType="incremental"
            type="versions"
            addedValues={versionsDraft.add.map(String)}
            removedValues={versionsDraft.remove.map(String)}
          />
        ) */}
        <IncrementalLaunchAvailChip
          icon={<PolarisIcon icon="rocket_launch" color="white" />}
          label="All Titles"
          launchType="first-time"
          items={[...versionItems].sort((a, b) => a.label.localeCompare(b.label))}
        />
      </StyledLeftCell>
      <StyledLeftCell>
        <StyledRelease>
          <StyledReleaseDate>{date}</StyledReleaseDate>
          <StyledSubText>{time}</StyledSubText>
        </StyledRelease>
      </StyledLeftCell>
      <StyledCenteredCell>
        {isIncrementalLaunch ? (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="map" color="white" />}
            label="Territories"
            launchType="incremental"
            type="territories"
            addedValues={territoriesDraft.add}
            removedValues={territoriesDraft.remove}
            allLanguagesMap={allLanguagesMap}
          />
        ) : (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="map" color="white" />}
            label="Territories"
            launchType="first-time"
            items={[...territoryItems].sort((a, b) => a.label.localeCompare(b.label))}
          />
        )}
      </StyledCenteredCell>
      <StyledCenteredCell>
        {isIncrementalLaunch ? (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="music" color="white" />}
            label="Audio"
            launchType="incremental"
            type="audio"
            addedValues={audioDraft.add}
            removedValues={audioDraft.remove}
            allLanguagesMap={allLanguagesMap}
          />
        ) : (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="music" color="white" />}
            label="Audio"
            launchType="first-time"
            items={[...dubItems].sort((a, b) => a.label.localeCompare(b.label))}
          />
        )}
      </StyledCenteredCell>
      <StyledCenteredCell>
        {isIncrementalLaunch ? (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="subtitles" color="white" />}
            label="Timed Text"
            launchType="incremental"
            type="timedtext"
            addedValues={timedtextDraft.add}
            removedValues={timedtextDraft.remove}
            allLanguagesMap={allLanguagesMap}
          />
        ) : (
          <IncrementalLaunchAvailChip
            icon={<PolarisIcon icon="subtitles" color="white" />}
            label="Timed Text"
            launchType="first-time"
            items={[...subItems].sort((a, b) => a.label.localeCompare(b.label))}
          />
        )}
      </StyledCenteredCell>
      <StyledActions>
        <OverflowMenu disabled={!onEdit && !onDelete}>
          {/* Kyle Temporarily remove edit as I'm not sure what it looks like in the new flow
          <StyledMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            disabled={!onEdit}
          >
            <PolarisIcon icon="edit" />
            <span>Edit</span>
          </StyledMenuItem> */}
          <StyledMenuItem onClick={promptDelete} disabled={!onDelete}>
            <PolarisIcon icon="delete_outline" />
            <span>Delete</span>
          </StyledMenuItem>
        </OverflowMenu>
      </StyledActions>
      {dialogVisible && dialog}
    </StyledRow>
  );
};

export default IncrementalLaunchDetailsRow;

const StyledRow = styled('div')<{ isEditing: number }>(({ theme, isEditing }) => ({
  display: 'grid',
  gridTemplateColumns: ROW_GRID_COLUMNS,
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 7),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: (theme as FocusTheme).palette.neutral[70],
  marginBottom: theme.spacing(0.5),
  border: isEditing ? `1px solid ${(theme as FocusTheme).palette.primary.main}` : 'none'
}));

const StyledLeftCell = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minWidth: 0
});

const StyledCenteredCell = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 0
});

const StyledRelease = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start'
});

const StyledReleaseDate = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: (theme as FocusTheme).palette.neutral[10]
}));

const StyledSubText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: (theme as FocusTheme).palette.neutral[30]
}));

const StyledActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center'
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  minWidth: 140
}));
